/**
 * S-18 «Отзывы и вопросы» — очередь, и S-19 «Отзыв» — карточка с черновиком ответа.
 * Штаб ответ не публикует: черновик правится здесь, копируется и публикуется в кабинете WB.
 * Имени покупателя нет: Штаб его не хранит.
 */
import { useState } from 'react'
import { fetchFeedback, fetchFeedbackItem, type FeedbackItem } from '../api/sections.js'
import { copyText, hapticSuccess } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { Empty, Failed, Loading, NoShop, Truncation, useAnnounce, useData, useScreenHead } from './common.js'
import { Sect, ToneTag } from './ui.js'
import type { ScreenId } from '../router/screens.js'

type Open = (id: ScreenId, param?: string) => void
type Kind = 'feedback' | 'question'

/** Параметр экрана S-19: у вопроса — с приставкой «q-», у отзыва — номер как есть. */
export const feedbackParam = (kind: Kind, id: string): string => (kind === 'question' ? 'q-' + id : id)
const parseParam = (p: string): { kind: Kind; id: string } => (p.startsWith('q-') ? { kind: 'question', id: p.slice(2) } : { kind: 'feedback', id: p })

/** Оценка звёздами: иконка и число — «★ 5», диктору — «оценка 5 из 5». */
function Stars({ n }: { n: number | null }): React.JSX.Element | null {
  if (n === null) return null
  return (
    <span className={'stars' + (n <= 3 ? ' stars-low' : '')}>
      <Icon name="content-rating" size={16} /><span aria-hidden="true">{n}</span><span className="vh">оценка {n} из 5</span>
    </span>
  )
}

function Row({ f, onOpen }: { f: FeedbackItem; onOpen: () => void }): React.JSX.Element {
  return (
    <button type="button" className="row row-tap fb-row" data-testid={'fb-' + f.id} onClick={onOpen}>
      <span className="row-main">
        <span className="fb-head"><Stars n={f.rating} /><span className="row-meta">{f.date_display}</span></span>
        <span className="row-title fb-text">{f.preview ?? 'Без текста — только оценка'}</span>
        <span className="row-meta">{f.product ?? (f.nm_id !== null ? `Артикул WB ${f.nm_id}` : '')}</span>
        {(f.draft_ready || f.answered) && (
          <span className="camp-tags">
            {f.draft_ready && !f.answered && <ToneTag tone="good" word="Черновик готов" />}
            {f.answered && <ToneTag tone="neutral" word="Ответ опубликован" />}
          </span>
        )}
      </span>
      <Icon name="nav-next" className="ic-chev row-end" />
    </button>
  )
}

export function ScreenFeedback({ open }: { open: Open }): React.JSX.Element {
  const [kind, setKind] = useState<Kind>('feedback')
  const [state, setState] = useState<'waiting' | 'answered'>('waiting')
  const { s, reload } = useData(() => fetchFeedback(kind, state), [kind, state])
  useScreenHead(undefined, null)
  const counts = s.kind === 'ok' && s.data.shop !== null ? s.data.counts : null
  const seg = <T extends string>(cur: T, set: (v: T) => void, v: T, label: string, n?: number, testid?: string): React.JSX.Element => (
    <button type="button" className={'seg' + (cur === v ? ' seg-on' : '')} aria-pressed={cur === v} data-testid={testid} onClick={() => set(v)}>
      {label}{n !== undefined && <span className="seg-n">{n}</span>}
    </button>
  )
  return (
    <div data-testid="screen-body">
      <div className="segs" role="group" aria-label="Что показать">
        {seg(kind, setKind, 'feedback', 'Отзывы', counts?.feedback_waiting, 'fb-kind-feedback')}
        {seg(kind, setKind, 'question', 'Вопросы', counts?.question_waiting, 'fb-kind-question')}
      </div>
      <div className="segs" role="group" aria-label="Какие">
        {seg(state, setState, 'waiting', 'Ждут ответа', undefined, 'fb-state-waiting')}
        {seg(state, setState, 'answered', 'С ответом', undefined, 'fb-state-answered')}
      </div>
      {s.kind === 'loading' && <Loading label="Загрузка отзывов" rows={5} />}
      {s.kind === 'fail' && <Failed message={s.message} onRetry={reload} />}
      {s.kind === 'ok' && (s.data.shop === null ? <NoShop reason={s.data.reason} /> : s.data.items.length === 0
        ? <section className="card empty-card" data-testid="empty">
            <Empty icon="agent-feedback"
              title={state === 'waiting' ? (kind === 'feedback' ? 'Новых отзывов без ответа нет' : 'Новых вопросов без ответа нет') : 'Ответов пока нет'}
              text={state === 'waiting' ? 'Как только покупатель напишет, агент подготовит черновик ответа к утреннему отчёту.' : 'Здесь будут отзывы, на которые магазин уже ответил.'} />
          </section>
        : <>
            <section className="card gap-top" data-testid="fb-list">
              {s.data.items.map((f) => <Row key={f.id} f={f} onOpen={() => open('S-19', feedbackParam(f.kind, f.id))} />)}
            </section>
            <Truncation note={s.data.truncation} />
          </>)}
    </div>
  )
}

export function ScreenFeedbackItem({ param, open }: { param: string; open: Open }): React.JSX.Element {
  const { kind, id } = parseParam(param)
  const { s, reload } = useData(() => fetchFeedbackItem(kind, id))
  useScreenHead(kind === 'question' ? 'Вопрос' : 'Отзыв', s.kind === 'ok' && s.data.shop !== null ? s.data.date_display : null)
  const announce = useAnnounce()
  const [text, setText] = useState<string | null>(null)
  if (s.kind === 'loading') return <Loading label="Загрузка отзыва" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const draft = text ?? d.draft?.text ?? ''
  const chars = [...draft].length
  const over = chars > d.limits.max
  const copy = async (): Promise<void> => {
    if (await copyText(draft)) { hapticSuccess(); announce({ kind: 'ok', text: 'Ответ скопирован — вставьте его в кабинете WB' }) }
    else announce({ kind: 'fail', text: 'Не получилось скопировать. Выделите текст в поле и скопируйте вручную.' })
  }
  return (
    <div data-testid="screen-body">
      <section className="card card-pad fb-card">
        <p className="fb-head"><Stars n={d.rating} /><span className="row-meta">{d.status}</span></p>
        {d.text.length === 0 && <p className="body-text muted">Без текста — только оценка.</p>}
        {d.text.map((t, k) => (
          <p className="body-text" key={k}>{t.label !== null && <b>{t.label}. </b>}{t.text}</p>
        ))}
        {(d.tags.length > 0 || d.photos_pcs > 0 || d.has_video) && (
          <p className="row-meta">
            {[d.tags.length > 0 ? `Отметки покупателя: ${d.tags.join(', ')}` : null,
              d.photos_pcs > 0 ? `фото: ${d.photos_pcs}` : null, d.has_video ? 'есть видео' : null].filter(Boolean).join(' · ')}
          </p>
        )}
        {d.warned && <p className="row-meta">Wildberries пометил вопрос как подозрительный — отвечайте нейтрально.</p>}
      </section>

      <Sect title="Товар" />
      <section className="card">
        <button type="button" className="row row-tap" data-testid="fb-product" disabled={d.nm_id === null}
          onClick={() => { if (d.nm_id !== null) open('S-12', String(d.nm_id)) }}>
          <span className="row-main">
            <span className="row-title">{d.product ?? 'Товар'}</span>
            <span className="row-meta">{[d.nm_id !== null ? `Артикул WB ${d.nm_id}` : null, d.article, d.variant].filter(Boolean).join(' · ')}</span>
          </span>
          {d.nm_id !== null && <Icon name="nav-next" className="ic-chev row-end" />}
        </button>
      </section>

      {d.answer !== null
        ? <>
            <Sect title="Ответ магазина" />
            <section className="card card-pad">
              <p className="body-text" data-testid="fb-answer">{d.answer}</p>
              {d.answer_note !== null && <p className="row-meta gap-top-s">{d.answer_note}</p>}
            </section>
          </>
        : <>
            <Sect title={d.draft !== null ? 'Черновик ответа' : 'Ответ'} aside={d.limits_display} />
            <section className="card card-pad">
              {d.draft === null && <p className="body-text muted">Агент готовит черновики к утреннему отчёту. Можно написать ответ самим.</p>}
              <label className="vh" htmlFor="fb-draft">Текст ответа</label>
              <textarea id="fb-draft" className="field-area" data-testid="fb-draft" rows={8} value={draft}
                onChange={(e) => setText(e.target.value)} aria-describedby="fb-count" />
              <p id="fb-count" className={'field-count' + (over ? ' is-over' : '')} aria-live="polite">
                {over ? `Длиннее на ${chars - d.limits.max} знаков` : `Осталось ${d.limits.max - chars} из ${d.limits.max} знаков`}
              </p>
              <button type="button" className="btn btn-block" data-testid="fb-copy" disabled={chars < d.limits.min} onClick={() => { void copy() }}>
                <Icon name="action-copy" size={20} />Скопировать ответ
              </button>
              <p className="card-foot card-foot-in">
                Опубликуйте ответ в кабинете WB: Штаб сам ничего не публикует. Ответ уйдёт на проверку Wildberries — обычно это занимает несколько часов.
              </p>
              {d.draft?.effect != null && <p className="row-meta">Агент: {d.draft.effect}</p>}
            </section>
          </>}
    </div>
  )
}
